/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React, { useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import get from 'lodash/get';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import DateInput from 'carbon-react/lib/components/date';
import { format } from 'date-fns';
import Icon from 'carbon-react/lib/components/icon';

import { UICarbonNumberField, UIFeatherIcon } from '@futrli/ui-components';
import { FormattedMessage, FormattedNumber, getCurrencySymbolFromCode, useIntl } from '@futrli/localisation';
import { UnpaidInvoicePayment, UnpaidInvoiceNextPaymentInfo } from '@futrli-sdk/predict';

import * as styles from './payments-modal.scss';
import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';
import { forceLocalDate, getDateWithStartOfDay } from '../../../utils/dates';
import { Data } from '../../../utils/table-filters/filter-table-types';
import {
  checkDatesOrder,
  checkFormDataCorrectlyFilled,
  formatDate,
  getDiffToInitialPayment,
  getInitialData,
  roundTwoDecimals
} from './helper';
import { updateUnpaidInvoiceById } from '../../../core/api/invoices';
import { penceToAmount } from '../../../core/formatters';
import { trackEvent } from '../../../core/tracker';
import { TRACKING_EVENTS } from '../../../core/constants';
import { useOnboardingUrlInfo } from '../../../forecast/onboarding/utils/hooks';
import { LS_LIVE_FORECAST_ID } from '../../../forecast/onboarding/const';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';
import { EditPaymentsContainer } from './edit-payments-container';

interface Props {
  openDialog: boolean;
  data?: Data;
  onCloseDialog: () => void;
  revalidateData?: () => Promise<void>;
}

enum SplitPaymentType {
  BY_VALUE = 'split-by-value',
  BY_PERCENTAGE = 'split-by-percent'
}

export interface SplitPayment {
  date?: Date;
  amount?: string;
  percent?: string;
}

export const EditPayments = ({ openDialog, onCloseDialog, data, revalidateData }: Props) => {
  const intl = useIntl();
  const payment: UnpaidInvoicePayment | null = get(data, 'payment') || null;
  const invoiceBillId: string | undefined = get(data, 'id');
  const nextPaymentInfo: UnpaidInvoiceNextPaymentInfo | undefined = (get(
    data,
    'next_payment_info'
  ) as unknown) as UnpaidInvoiceNextPaymentInfo;
  const { isOnboardingScenarioWithInvoicesRoute } = useOnboardingUrlInfo();

  const totalAmountPayable: number = penceToAmount(get(data, 'total_payable') || 0);

  const [hasChanges, setHasChanges] = useState(false);

  const { forecast } = usePredictionsInfoContext();

  // const [splitPaymentType, setSplitPaymentType] = useState(
  //   payment ? (payment.kind as SplitPaymentType) : SplitPaymentType.BY_VALUE
  // );
  const [splitPayments, setSplitPayments] = useState<SplitPayment[]>(
    getInitialData(payment, data, forecast?.forecast_from_date)
  );

  const [diffToInitialPayment, setDiffToInitialPayment] = useState(0);

  const [dateError, setDateError] = useState(false);

  const [formIncorrectlyFilled, setFormIncorrectlyFilled] = useState(false);

  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId, forecastId: tempForecastId } = useParams();
  const organisationId = tempOrganisationId || '';
  const workspaceId = tempWorkspaceId || '';
  const forecastId = tempForecastId || '';

  const { currency, addErrorMessage } = useContext(PredictManagementContext);

  const currencySymbol = getCurrencySymbolFromCode(currency!);

  // const splitPaymentTypes = [
  //   {
  //     value: SplitPaymentType.BY_VALUE,
  //     name: intl.formatMessage({ id: 'predict.by_value', defaultMessage: 'By value' })
  //   },
  //   {
  //     value: SplitPaymentType.BY_PERCENTAGE,
  //     name: intl.formatMessage({ id: 'predict.by_percentage', defaultMessage: 'By percentage' })
  //   }
  // ];

  // const handleSplitPaymentTypeChange = useCallback(
  //   (event: React.ChangeEvent<HTMLInputElement>) => setSplitPaymentType(event.target.value as SplitPaymentType),
  //   []
  // );

  const handleAddPaymentClick = useCallback(() => {
    setHasChanges(true);
    setSplitPayments([
      ...splitPayments,
      {
        amount: undefined,
        date: undefined,
        percent: undefined
      }
    ]);
  }, [splitPayments]);

  const handleDeletePaymentClick = useCallback(
    (index: number) => {
      setHasChanges(true);
      const newSplitPayments = [...splitPayments.slice(0, index), ...splitPayments.slice(index + 1)];
      setSplitPayments([...splitPayments.slice(0, index), ...splitPayments.slice(index + 1)]);
      const datesAreValid = checkDatesOrder(
        newSplitPayments.filter(p => typeof p.date !== 'undefined').map(p => p.date!)
      );

      setDateError(!datesAreValid);

      setFormIncorrectlyFilled(!checkFormDataCorrectlyFilled(newSplitPayments));

      if (data && typeof totalAmountPayable === 'number') {
        setDiffToInitialPayment(getDiffToInitialPayment(newSplitPayments, totalAmountPayable));
      }
    },
    [splitPayments, data, totalAmountPayable]
  );

  const handleAmountChange = useCallback(
    (value: string, indexToUpdate: number) => {
      setHasChanges(true);
      if (data && typeof totalAmountPayable === 'number') {
        const newSplitPayments = splitPayments.map((sp, index) => {
          if (index === indexToUpdate) {
            return {
              ...sp,
              amount: value,
              percent: String(roundTwoDecimals(Number(value) / totalAmountPayable! + Number.EPSILON))
            };
            // if (splitPaymentType === SplitPaymentType.BY_VALUE) {
            //   return {
            //     ...sp,
            //     amount: value,
            //     percent: String(roundTwoDecimals(Number(value) / totalAmountPayable! + Number.EPSILON))
            //   };
            // } else {
            //   return {
            //     ...sp,
            //     amount: String(roundTwoDecimals((Number(value) / 100) * totalAmountPayable!)),
            //     percent: String(roundTwoDecimals(Number(value) / 100))
            //   };
            // }
          }
          return { ...sp };
        });
        setSplitPayments(newSplitPayments);

        setDiffToInitialPayment(getDiffToInitialPayment(newSplitPayments, totalAmountPayable));

        setFormIncorrectlyFilled(!checkFormDataCorrectlyFilled(newSplitPayments));
      }
    },
    [splitPayments, data, totalAmountPayable]
  );

  const handleDateChange = useCallback(
    (date: Date, indexToUpdate: number) => {
      setHasChanges(true);
      const newSplitPayments = splitPayments.map((sp, index) => {
        if (index === indexToUpdate) {
          return {
            ...sp,
            date: getDateWithStartOfDay(date)
          };
        }
        return { ...sp };
      });

      setSplitPayments(newSplitPayments);

      const datesAreValid = checkDatesOrder(
        newSplitPayments.filter(p => typeof p.date !== 'undefined').map(p => p.date!)
      );

      setDateError(!datesAreValid);

      setFormIncorrectlyFilled(!checkFormDataCorrectlyFilled(newSplitPayments));
    },
    [splitPayments]
  );

  const handleSaveClick = useCallback(async () => {
    let forecastIdToUse;
    const liveForecastIdLocalStorage = localStorage.getItem(LS_LIVE_FORECAST_ID);

    if (isOnboardingScenarioWithInvoicesRoute && liveForecastIdLocalStorage) {
      forecastIdToUse = liveForecastIdLocalStorage;
    } else {
      forecastIdToUse = forecastId as string;
    }

    if (!forecastIdToUse) {
      forecastIdToUse = forecast?.id;
    }
    if (forecastIdToUse && invoiceBillId) {
      const newPayment = {
        id: invoiceBillId,
        kind: SplitPaymentType.BY_VALUE,
        items: splitPayments
          .filter(
            sp =>
              typeof sp.amount !== 'undefined' && typeof sp.date !== 'undefined' && typeof sp.percent !== 'undefined'
          )
          .map(sp => ({
            amount: Math.round(Number(sp.amount) * 100),
            percent: Number(sp.percent),
            date: formatDate(sp.date!)
          }))
      };

      try {
        await updateUnpaidInvoiceById(
          { payment: newPayment as UnpaidInvoicePayment },
          workspaceId,
          organisationId,
          forecastIdToUse,
          invoiceBillId
        );
        trackEvent(TRACKING_EVENTS.EDITED_INVOICE);
        await revalidateData?.();
        onCloseDialog();
      } catch (e) {
        addErrorMessage?.(new Error('Failure to update invoice'));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitPayments, workspaceId, organisationId, forecastId, invoiceBillId]);

  if (!totalAmountPayable) {
    return null;
  }

  return (
    <EditPaymentsContainer
      isOpen={openDialog}
      onClose={onCloseDialog}
      aria-label={intl.formatMessage({
        id: 'predict.predictions.edit_payment_dates',
        defaultMessage: 'Edit payment dates'
      })}
    >
      <Form
        stickyFooter
        leftSideButtons={
          <Button buttonType="secondary" onClick={onCloseDialog}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
        }
        saveButton={
          <Button
            buttonType="primary"
            onClick={handleSaveClick}
            disabled={diffToInitialPayment !== 0 || dateError || formIncorrectlyFilled}
          >
            <FormattedMessage id="common.save" defaultMessage="Save" />
          </Button>
        }
        onSubmit={handleSaveClick}
      >
        {/* <UITextField
            onChange={handleSplitPaymentTypeChange}
            id="role"
            value={splitPaymentType}
            select={true}
            className={styles.selectType}
            inputProps={{
              className: styles.formInputInner
            }}
          >
            {splitPaymentTypes.map(o => (
              <UIMenuItem key={o.value} variant="forSelect" value={o.value}>
                {o.name}
              </UIMenuItem>
            ))}
          </UITextField> */}
        <div className={styles.totalAmountLabel} style={{ marginBottom: 6 }}>
          <FormattedMessage
            id="predict.contact_name_value"
            defaultMessage="Contact: {contact}"
            values={{
              contact: <span style={{ fontWeight: 500 }}>{data?.contact_name}</span>
            }}
          />
        </div>
        {data?.invoice_number ? (
          <div className={styles.totalAmountLabel} style={{ marginBottom: 6 }}>
            <FormattedMessage
              id="predict.reference_ref_value"
              defaultMessage="Reference: {reference}"
              values={{
                reference: <span style={{ fontWeight: 500 }}>{data?.invoice_number}</span>
              }}
            />
          </div>
        ) : null}
        <div className={styles.totalAmountLabel}>
          <FormattedMessage
            id="predict.total_amount_value"
            defaultMessage="Total amount: {totalAmount}"
            values={{
              totalAmount: (
                <span style={{ fontWeight: 500 }}>
                  <FormattedNumber style="currency" currency={currency!} value={totalAmountPayable} />
                </span>
              )
            }}
          />
        </div>
        <div>
          {splitPayments.map((sp, index) => (
            <div key={index} className={styles.payment}>
              <div className={styles.flexContainer}>
                <FormattedMessage
                  id="predict.payment_n"
                  defaultMessage="Payment {paymentNum}"
                  values={{ paymentNum: index + 1 }}
                />
                <div>
                  <div className={styles.byValueAmountInput}>
                    <UICarbonNumberField
                      mb={0}
                      autoComplete="off"
                      // clampMax={splitPaymentType === SplitPaymentType.BY_PERCENTAGE ? MAX_PERCENTAGE : undefined}
                      // clampMin={splitPaymentType === SplitPaymentType.BY_PERCENTAGE ? 0 : undefined}
                      // className={classNames({
                      //   [styles.byPercentageAmountInput]: splitPaymentType === SplitPaymentType.BY_PERCENTAGE,
                      //   [styles.byValueAmountInput]: splitPaymentType === SplitPaymentType.BY_VALUE
                      // })}
                      currencySymbol={currencySymbol}
                      onChange={(value: string) => {
                        handleAmountChange(value, index);
                      }}
                      placeholder="0"
                      // type={splitPaymentType === SplitPaymentType.BY_VALUE ? 'currency' : 'percentage'}
                      type="currency"
                      // value={
                      //   sp.percent && sp.amount
                      //     ? splitPaymentType === SplitPaymentType.BY_VALUE
                      //       ? sp.amount
                      //       : String(Number(sp.percent) * 100)
                      //     : ''
                      // }
                      value={sp.amount ? sp.amount : ''}
                      error={diffToInitialPayment !== 0 || (formIncorrectlyFilled && !sp.amount)}
                    />
                  </div>
                  {index === splitPayments.length - 1 ? (
                    <div
                      // className={classNames(styles.lineContainer, {
                      //   [styles.lineContainerByValue]: splitPaymentType === SplitPaymentType.BY_VALUE,
                      //   [styles.lineContainerByPercentage]: splitPaymentType === SplitPaymentType.BY_PERCENTAGE
                      // })}
                      className={classNames(styles.lineContainer, styles.lineContainerByValue)}
                    >
                      <div className={styles.addLine}>
                        <div className={styles.addIcon} onClick={handleAddPaymentClick}>
                          <UIFeatherIcon iconName="plus" />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
                {/* {splitPaymentType === SplitPaymentType.BY_PERCENTAGE && typeof sp.percent === 'string' && (
                  <FormattedNumber style="currency" currency={currency!} value={Number(sp.amount)} />
                )} */}
              </div>
              <div className={styles.flexContainer}>
                <FormattedMessage id="common.on_date" defaultMessage="on" />
                <DateInput
                  id="toDate"
                  onChange={e => handleDateChange(forceLocalDate(e.target.value.rawValue || ''), index)}
                  value={sp.date ? format(sp.date, 'dd/MM/yyyy') : ''}
                  error={dateError || (formIncorrectlyFilled && !sp.date)}
                  aria-label={intl.formatMessage({
                    id: 'common.select_payment_date.aria_label',
                    defaultMessage: 'Select payment date'
                  })}
                  mb={0}
                  ml="12px"
                  minDate={forecast?.forecast_from_date || undefined}
                />
                <div className={styles.iconButtonContainer}>
                  <Button
                    buttonType="tertiary"
                    size="small"
                    ml="12px"
                    p={0}
                    onClick={() => handleDeletePaymentClick(index)}
                    disabled={splitPayments.length === 1}
                    aria-label={intl.formatMessage({
                      id: 'common.delete_payment.aria_label',
                      defaultMessage: 'Delete payment'
                    })}
                  >
                    <Icon type="delete" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ height: 24 }} />
        {diffToInitialPayment !== 0 &&
          (diffToInitialPayment > 0 ? (
            <div style={{ marginTop: 48 }}>
              <div className={clsx('tag is-danger is-light')}>
                <FormattedMessage
                  id="predict.entered_amount_not_match_initial"
                  defaultMessage="Entered amounts don't match initial payment amount."
                />
                <span style={{ marginLeft: 4 }}>
                  <FormattedMessage
                    id="predict.amount_left_value"
                    defaultMessage="Amount left: {amountLeft}"
                    values={{
                      amountLeft: <FormattedNumber value={diffToInitialPayment} style="currency" currency={currency!} />
                    }}
                  />
                </span>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 48 }}>
              <div className={clsx('tag is-danger is-light')}>
                <FormattedMessage
                  id="predict.entered_amount_not_match_initial"
                  defaultMessage="Entered amounts don't match initial payment amount."
                />
                <span style={{ marginLeft: 4 }}>
                  <FormattedMessage
                    id="predict.amount_over_by_value"
                    defaultMessage="Amount over by: {amountOver}"
                    values={{
                      amountOver: (
                        <FormattedNumber value={Math.abs(diffToInitialPayment)} style="currency" currency={currency!} />
                      )
                    }}
                  />
                </span>
              </div>
            </div>
          ))}

        {dateError && (
          <div style={{ marginTop: diffToInitialPayment !== 0 ? 16 : 48 }}>
            <div className={clsx('tag is-danger is-light')}>
              <FormattedMessage
                id="predict.dates_need_chronological_order"
                defaultMessage="Dates need to be in chronological order."
              />
            </div>
          </div>
        )}

        {formIncorrectlyFilled && (
          <div style={{ marginTop: diffToInitialPayment !== 0 || dateError ? 16 : 48 }}>
            <div className={clsx('tag is-danger is-light')}>
              <FormattedMessage id="predict.please_correct_errors" defaultMessage="Please correct errors" />
            </div>
          </div>
        )}

        {nextPaymentInfo?.is_overdue && !hasChanges && (
          <div style={{ marginTop: diffToInitialPayment !== 0 || dateError ? 16 : 48 }}>
            <div className={clsx('tag is-danger is-light')}>
              <FormattedMessage
                id="predict.expected_payment_overdue_please_alter"
                defaultMessage="An expected payment is now overdue. Please alter the payments or dates above to reschedule."
              />
            </div>
          </div>
        )}
      </Form>
    </EditPaymentsContainer>
  );
};
